import {createHash} from "sha1-uint8array"

enum TypeTag {
    Undefined = 0,
    Null = 1,
    Boolean = 2,
    Number = 3,
    String = 4,
    Object = 5,
    Array = 6,
    ArrayBuffer = 7,
    ArrayBufferView = 8,
    Reference = 9,
    ReferenceHash = 10,
}

class IncrementalHasher {
    private hasher = createHash()
    private buf = new ArrayBuffer(9)
    private dv = new DataView(this.buf)
    private buf1 = new Uint8Array(this.buf, 0, 1)
    private buf2 = new Uint8Array(this.buf, 0, 2)
    private buf5 = new Uint8Array(this.buf, 0, 5)
    private buf9 = new Uint8Array(this.buf, 0, 9)

    reset() {
        //TODO: the sha1-uint8array library does not currently support resetting the hasher state,
        // so we have to create a new hasher instance for each hash. Ideally we would reuse the same instance
        // and just reset its state here, but that would require modifying or replacing the library.
        this.hasher = createHash()
    }

    updateUndefined() {
        this.dv.setUint8(0, TypeTag.Undefined)
        this.hasher.update(this.buf1)
    }

    updateBoolean(value: boolean) {
        this.dv.setUint8(0, TypeTag.Boolean)
        this.dv.setUint8(1, value ? 1 : 0)
        this.hasher.update(this.buf2)
    }

    updateNumber(value: number) {
        this.dv.setUint8(0, TypeTag.Number)
        this.dv.setFloat64(1, value)
        this.hasher.update(this.buf9)
    }

    updateString(value: string) {
        this.dv.setUint8(0, TypeTag.String)
        this.dv.setUint32(1, value.length)
        this.hasher.update(this.buf5)
        this.hasher.update(value)
    }

    updateNull() {
        this.dv.setUint8(0, TypeTag.Null)
        this.hasher.update(this.buf1)
    }

    updateReference(refId: number) {
        this.dv.setUint8(0, TypeTag.Reference)
        this.dv.setUint32(1, refId)
        this.hasher.update(this.buf5)
    }

    updateReferenceHash(hash: string) {
        this.dv.setUint8(0, TypeTag.ReferenceHash)
        this.dv.setUint32(1, hash.length)
        this.hasher.update(this.buf5)
        this.hasher.update(hash)
    }

    updateArrayHeader(length: number) {
        this.dv.setUint8(0, TypeTag.Array)
        this.dv.setUint32(1, length)
        this.hasher.update(this.buf5)
    }

    updateArrayBuffer(arrayBuffer: ArrayBuffer) {
        this.dv.setUint8(0, TypeTag.ArrayBuffer)
        this.dv.setUint32(1, arrayBuffer.byteLength)
        this.hasher.update(this.buf5)
        this.hasher.update(new Uint8Array(arrayBuffer))
    }

    updateArrayBufferView(view: ArrayBufferView) {
        this.dv.setUint8(0, TypeTag.ArrayBufferView)
        this.dv.setUint32(1, view.byteLength)
        this.hasher.update(this.buf5)
        this.hasher.update(view)
    }

    updateObjectHeader(numKeys: number) {
        this.dv.setUint8(0, TypeTag.Object)
        this.dv.setUint32(1, numKeys)
        this.hasher.update(this.buf5)
    }

    digestHex() {
        return this.hasher.digest("hex")
    }
}

export function hashObject(obj: object | null, considerUndefinedObjectValues: boolean = false): string {
    const hasher = new IncrementalHasher()
    let nodeIdCounter = 0
    const visited = new Map<unknown, number>()

    const traverse = (data: any) => {
        const thisNodeId = nodeIdCounter++
        const type = typeof data
        switch (type) {
            case "undefined":
                hasher.updateUndefined()
                return
            case "boolean":
                hasher.updateBoolean(data)
                return
            case "number":
                hasher.updateNumber(data)
                return
            case "string":
                hasher.updateString(data)
                return
            case "object": {
                if (data === null) {
                    hasher.updateNull()
                    return
                }
                const existingRef = visited.get(data)
                if (existingRef !== undefined) {
                    hasher.updateReference(existingRef)
                    return
                }
                visited.set(data, thisNodeId)
                if (Array.isArray(data)) {
                    hasher.updateArrayHeader(data.length)
                    for (const item of data) {
                        traverse(item)
                    }
                } else if (data instanceof ArrayBuffer) {
                    hasher.updateArrayBuffer(data)
                } else if (ArrayBuffer.isView(data)) {
                    hasher.updateArrayBufferView(data)
                } else {
                    //TODO: this is not safe for general object hashing. The caller should opt in to this behavior, or specify a custom hash shortcut function.
                    if ((data as object).hasOwnProperty("hash")) traverse(data.hash)
                    else {
                        const keys = Object.keys(data)
                        const sortedKeys = (considerUndefinedObjectValues ? keys : keys.filter((key) => data[key] !== undefined)).sort()
                        hasher.updateObjectHeader(sortedKeys.length)
                        for (const key of sortedKeys) {
                            traverse(key)
                            traverse(data[key])
                        }
                    }
                }
                return
            }
        }
        throw new Error(`Unsupported value type in hash: ${type}`)
    }
    traverse(obj)
    return hasher.digestHex()
}

/**
 * Whether a value is a nested graph node that should be recursed into and memoized, as opposed to a leaf.
 * `null` and binary leaves (`ArrayBuffer` / typed arrays) are hashed inline by `updateHasherForShallowValue`
 * rather than traversed, so they are excluded here.
 */
const isTraversableNode = (value: unknown): boolean =>
    typeof value === "object" && value !== null && !(value instanceof ArrayBuffer) && !ArrayBuffer.isView(value)

/**
 * Incrementally hashes an object graph (a DAG), memoizing each node's hash so a node referenced from multiple
 * places (a diamond) is hashed only once. The input MUST be acyclic: cycles are detected and rejected rather than
 * recursed into forever (see `inProgress`). Binary leaves (`ArrayBuffer` / typed arrays) are hashed inline; a
 * top-level binary value is rejected — wrap it in an object or array.
 *
 * A single `IncrementalHasher` is shared across the whole traversal. This is only safe because of the strict
 * two-phase structure of `traverse`:
 *   - Phase 1 fully hashes and caches every nested node (recursively) BEFORE this node's own digest begins.
 *   - Phase 2 builds this node's digest, and for each nested node it only READS the already-cached hash via
 *     `traverse(value, false)`, which short-circuits on the cache and never touches the shared hasher.
 * Because phase 2 never re-enters a hashing computation, the in-progress digest can't be corrupted, and the
 * `reset()` at the start of each phase 2 gives every node a clean hasher state. Keep this invariant intact: any
 * code that hashes a nested node from inside phase 2 must go through the cache (allowUncached = false).
 */
export class GraphNodeHasher<T extends object = object> {
    private cache = new Map<T, string>()
    private inProgress = new Set<T>()
    private hasher = new IncrementalHasher()

    /**
     * @param considerUndefinedObjectValues when false (the default), object keys whose value is `undefined` are
     *   stripped before hashing, so `{a: 1, b: undefined}` hashes identically to `{a: 1}` (matching `hashObject`'s
     *   default). When true, such keys are kept and hashed as an explicit `undefined`. Array elements are never
     *   stripped, regardless of this flag.
     */
    constructor(private considerUndefinedObjectValues: boolean = false) {}

    private updateHasherForShallowValue(type: string, value: any): void {
        switch (type) {
            case "undefined":
                this.hasher.updateUndefined()
                break
            case "boolean":
                this.hasher.updateBoolean(value)
                break
            case "number":
                this.hasher.updateNumber(value)
                break
            case "string":
                this.hasher.updateString(value)
                break
            case "object":
                if (value === null) {
                    this.hasher.updateNull()
                } else if (value instanceof ArrayBuffer) {
                    this.hasher.updateArrayBuffer(value)
                } else if (ArrayBuffer.isView(value)) {
                    this.hasher.updateArrayBufferView(value)
                } else {
                    throw new Error(`Unsupported object type for shallow hash`)
                }
                break
            default:
                throw new Error(`Unsupported value type in hash: ${type}`)
        }
    }

    private traverse(obj: T, allowUncached: boolean = true): string {
        const existingHash = this.cache.get(obj)
        if (existingHash !== undefined) {
            return existingHash
        } else if (!allowUncached) {
            throw new Error(`Object hash expected to be cached but was not found`)
        } else if (this.inProgress.has(obj)) {
            // we re-entered a node that is still mid-traversal further up the stack: the graph contains a cycle
            throw new Error(`Cycle detected while hashing graph node; GraphNodeHasher requires an acyclic graph`)
        }
        // mark as in-progress so a back-edge to this node is detected as a cycle instead of recursing forever;
        // removed in `finally` so the marker is cleared on both success and error (e.g. a deeper cycle/throw)
        this.inProgress.add(obj)
        try {
            if (Array.isArray(obj)) {
                // first visit all nested objects to populate the cache with their hashes
                for (const item of obj) {
                    if (isTraversableNode(item)) {
                        this.traverse(item)
                    }
                }
                // then hash this object, using cached hashes for nested objects
                this.hasher.reset()
                this.hasher.updateArrayHeader(obj.length)
                for (const item of obj) {
                    if (isTraversableNode(item)) {
                        this.hasher.updateReferenceHash(this.traverse(item, false))
                    } else {
                        this.updateHasherForShallowValue(typeof item, item)
                    }
                }
                // populate cache
                const hash = this.hasher.digestHex()
                this.cache.set(obj, hash)
                return hash
            } else if (obj instanceof ArrayBuffer) {
                throw new Error(`ArrayBuffers must be wrapped in an object to be hashed with GraphNodeHasher`)
            } else if (ArrayBuffer.isView(obj)) {
                throw new Error(`ArrayBufferViews must be wrapped in an object to be hashed with GraphNodeHasher`)
            } else {
                // first visit all nested objects to populate the cache with their hashes
                for (const value of Object.values(obj)) {
                    if (isTraversableNode(value)) {
                        this.traverse(value)
                    }
                }
                // then hash this object, using cached hashes for nested objects
                this.hasher.reset()
                const allKeys = Object.keys(obj)
                const keys = (this.considerUndefinedObjectValues ? allKeys : allKeys.filter((key) => (obj as any)[key] !== undefined)).sort()
                this.hasher.updateObjectHeader(keys.length)
                for (const key of keys) {
                    const value = (obj as any)[key]
                    this.updateHasherForShallowValue(typeof key, key)
                    if (isTraversableNode(value)) {
                        this.hasher.updateReferenceHash(this.traverse(value, false))
                    } else {
                        this.updateHasherForShallowValue(typeof value, value)
                    }
                }
                // populate cache
                const hash = this.hasher.digestHex()
                this.cache.set(obj, hash)
                return hash
            }
        } finally {
            this.inProgress.delete(obj)
        }
    }

    hash(obj: T): string {
        return this.traverse(obj)
    }

    boundHashFn = this.hash.bind(this)
}
